import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable, catchError } from 'rxjs';
import { StaticSettings } from './static-settings';
import { CustomerSearchFilter } from '../interfaces/customer-search-filter';
import { CustomerHeader } from '../interfaces/customer-header';

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private readonly customersSubject = new BehaviorSubject<CustomerHeader[]>([]);
  public customers$ = this.customersSubject.asObservable();

  protected _http = inject(HttpClient);
  protected _settings = inject(StaticSettings);

  constructor() {
    this.fetchCustomers();
  }

  fetchCustomers(): void {
    this.getLastAccessedCustomer({ take: 10 })
      .pipe(
        catchError(error => {
          console.error('Error fetching customers', error);
          return [];
        })
      )
      .subscribe(customers => {
        this.customersSubject.next(customers); // Update the BehaviorSubject with the new data
      });
  }

  public getCustomerMaster(
    state: State,
    customerFilter: CustomerSearchFilter
  ): Observable<CustomerHeader[]> {
    const endpointUrl = this._settings.getApiUrl(`customer/master`);
    const params = this.buildParameters(state, customerFilter);
    return this._http.get<CustomerHeader[]>(endpointUrl, { params: params });
  }

  public getCustomerMasterCount(customerFilter: CustomerSearchFilter): Observable<number> {
    const endpointUrl = this._settings.getApiUrl(`customer/master/count`);
    const params = this.buildParameters(null, customerFilter);
    return this._http.get<number>(endpointUrl, { params: params });
  }

  public setLastAccessedCustomer(
    customerId: string,
    tenantId: string,
    clientType: number
  ): Observable<any> {
    const endpointUrl = this._settings.getApiUrl(`usercenter/setLastCustomers`);
    const bodyParams = {};
    bodyParams['CustomerVirtualGroupID'] = customerId;
    bodyParams['ClientType'] = clientType;
    bodyParams['TenantId'] = tenantId;
    return this._http.post(endpointUrl, bodyParams, { params: null });
  }

  public getLastAccessedCustomer(state: State): Observable<CustomerHeader[]> {
    const endpointUrl = this._settings.getApiUrl(`usercenter/lastCustomers`);

    let params = new HttpParams();
    if (state.skip) {
      params = params.set('offset', state.skip.toString());
    }
    if (state.take) {
      params = params.set('limit', state.take.toString());
    }
    if (state?.sort?.[0]) {
      params = params.set('orderBy', state.sort[0].field);
      params =
        state.sort[0].dir === 'desc'
          ? params.set('ascending', 'false')
          : params.set('ascending', 'true');
    }
    return this._http.get<CustomerHeader[]>(endpointUrl, { params });
  }

  private buildParameters(state: State, customerFilter: CustomerSearchFilter) {
    let params: HttpParams = new HttpParams();
    if (customerFilter !== null && customerFilter !== undefined) {
      Object.entries(customerFilter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value.trim() !== '') {
          params = params.set(key, value);
        }
      });
    }
    if (state !== null) {
      if (state.skip) {
        params = params.set('offset', state.skip.toString());
      }
      if (state.take) {
        params = params.set('limit', state.take.toString());
      }
      if (state?.sort?.[0]) {
        params = params.set('orderBy', state.sort[0].field);
        params =
          state.sort[0].dir === 'desc'
            ? params.set('ascending', 'false')
            : params.set('ascending', 'true');
      }
    }
    return params;
  }
}
